import { useState, useEffect } from 'react';
import {
  getLatestDebtSurvey, getOpenDebtFindings, getDebtSurveys,
  generateDebtBriefingAction, getLatestBriefing,
} from '../../../services/firestore/opsService';
import type { DebtSurvey, DebtFinding, DebtType, DebtSeverity } from '../../../types/ops';

const C = {
  text: '#18181B',
  textSec: '#52525B',
  textMuted: '#A1A1AA',
  rose: '#E11D48',
  roseSoft: '#FFF1F2',
  pass: '#059669',
  passBg: '#D1FAE5',
  warn: '#B45309',
  warnBg: '#FEF3C7',
  fail: '#E11D48',
  failBg: '#FFF1F2',
  bgCard: '#FFFFFF',
  border: '#E4E4E7',
};

const DEBT_TYPE_LABELS: Record<DebtType, string> = {
  HARDCODED_VALUE: 'Hardcoded Value',
  STUBBED_FUNCTION: 'Stubbed Function',
  TYPE_WORKAROUND: 'Type Workaround',
  MISSING_ERROR_HANDLING: 'Missing Error Handling',
  PLACEHOLDER_UI: 'Placeholder UI',
  DEAD_CODE: 'Dead Code',
  HARDCODED_PROMPT: 'Hardcoded Prompt',
  STALE_REFERENCE: 'Stale Reference',
  MISSING_BRAND_SCOPING: 'Missing Brand Scoping',
  SECURITY_GAP: 'Security Gap',
};

const SEVERITY_CONFIG: Record<DebtSeverity, { color: string; bg: string; label: string }> = {
  critical: { color: C.fail, bg: C.failBg, label: 'Critical' },
  high: { color: C.warn, bg: C.warnBg, label: 'High' },
  medium: { color: '#6D28D9', bg: '#EDE9FE', label: 'Medium' },
  low: { color: C.textSec, bg: '#F4F4F5', label: 'Low' },
};

interface Briefing {
  id: string;
  surveyId: string;
  executiveSummary: {
    debtScore: number; scoreLabel: string; totalFindings: number;
    filesScanned: number; topPriority: string; recommendation: string;
  };
  sections: {
    priority: number; title: string; rationale: string;
    findings: { file: string; line?: number; description: string; suggestedFix?: string }[];
  }[];
  hotspots: { file: string; count: number }[];
  generatedAt: string;
}

export function CodeDebtTab() {
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState<DebtSurvey | null>(null);
  const [findings, setFindings] = useState<DebtFinding[]>([]);
  const [history, setHistory] = useState<DebtSurvey[]>([]);
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [viewMode, setViewMode] = useState<'overview' | 'findings' | 'hotspots' | 'briefing' | 'history'>('overview');

  useEffect(() => {
    (async () => {
      try {
        const [latestSurvey, openFindings, surveyHistory, latestBriefing] = await Promise.all([
          getLatestDebtSurvey(),
          getOpenDebtFindings(),
          getDebtSurveys(10),
          getLatestBriefing(),
        ]);
        setSurvey(latestSurvey);
        setFindings(openFindings);
        setHistory(surveyHistory);
        setBriefing(latestBriefing as Briefing | null);
      } catch (err) {
        console.error('Failed to load debt data:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60, color: C.textMuted, fontSize: 14 }}>Loading codebase intelligence...</div>;
  }

  if (!survey) {
    return (
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 40, textAlign: 'center' }}>
        <p style={{ fontSize: 16, color: C.text, fontWeight: 600, marginBottom: 8 }}>No debt survey data yet</p>
        <p style={{ fontSize: 14, color: C.textSec, marginBottom: 16 }}>
          Run the scanner locally to populate: <code style={{ background: '#F4F4F5', padding: '2px 6px', borderRadius: 3, fontSize: 12 }}>node scripts/scanDebt.cjs</code>
        </p>
        <p style={{ fontSize: 13, color: C.textMuted }}>
          Then submit the results via the Cloud Function to see data here.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        {(['overview', 'findings', 'hotspots', 'briefing', 'history'] as const).map((mode) => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: viewMode === mode ? 600 : 400,
              color: viewMode === mode ? C.rose : C.textSec,
              background: viewMode === mode ? C.roseSoft : 'transparent',
              border: `1px solid ${viewMode === mode ? C.rose : C.border}`,
              borderRadius: 4,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {mode}
          </button>
        ))}
      </div>

      {viewMode === 'overview' && <OverviewView survey={survey} findingsCount={findings.length} />}
      {viewMode === 'findings' && <FindingsView findings={findings} />}
      {viewMode === 'hotspots' && <HotspotsView survey={survey} />}
      {viewMode === 'briefing' && <BriefingView briefing={briefing} surveyId={survey.id} onBriefingGenerated={setBriefing} />}
      {viewMode === 'history' && <HistoryView history={history} />}
    </div>
  );
}

// ── Overview View ──

function OverviewView({ survey, findingsCount }: { survey: DebtSurvey; findingsCount: number }) {
  const scoreColor = survey.debtScore >= 80 ? C.pass : survey.debtScore >= 50 ? C.warn : C.fail;
  const scoreBg = survey.debtScore >= 80 ? C.passBg : survey.debtScore >= 50 ? C.warnBg : C.failBg;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
      {/* Debt Score */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 24, display: 'flex', alignItems: 'center', gap: 20 }}>
        <DebtScoreRing score={survey.debtScore} color={scoreColor} />
        <div>
          <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>Debt Score</p>
          <p style={{
            fontSize: 13, fontWeight: 600, color: scoreColor, background: scoreBg,
            display: 'inline-block', padding: '2px 8px', borderRadius: 4, marginTop: 4,
          }}>
            {survey.debtScore >= 80 ? 'CLEAN' : survey.debtScore >= 50 ? 'MODERATE' : 'HIGH DEBT'}
          </p>
        </div>
      </div>

      {/* Open Findings */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 24 }}>
        <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Open Findings</p>
        <p style={{ fontSize: 28, fontWeight: 700, color: findingsCount > 0 ? C.warn : C.pass }}>{findingsCount}</p>
        <p style={{ fontSize: 13, color: C.textSec, marginTop: 4 }}>
          {survey.resolved} resolved &middot; {survey.newSinceLastSurvey} new since last scan
        </p>
      </div>

      {/* Last Scan */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 24 }}>
        <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Last Survey</p>
        <p style={{ fontSize: 16, fontWeight: 600, color: C.text }}>
          {new Date(survey.scannedAt).toLocaleDateString()}
        </p>
        <p style={{ fontSize: 13, color: C.textSec, marginTop: 4 }}>
          Phase: {survey.phase} &middot; by {survey.scannedBy}
        </p>
      </div>

      {/* Breakdown by Type — full width */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 24, gridColumn: '1 / -1' }}>
        <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
          Findings by Type
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
          {(Object.entries(survey.byType) as [DebtType, number][])
            .filter(([, count]) => count > 0)
            .sort(([, a], [, b]) => b - a)
            .map(([type, count]) => (
              <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: '#FAFAFA', borderRadius: 4 }}>
                <span style={{ fontSize: 13, color: C.textSec }}>{DEBT_TYPE_LABELS[type]}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{count}</span>
              </div>
            ))}
          {Object.values(survey.byType).every((v) => v === 0) && (
            <p style={{ fontSize: 13, color: C.textMuted, gridColumn: '1 / -1' }}>No findings detected.</p>
          )}
        </div>
      </div>

      {/* Breakdown by Severity — full width */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 24, gridColumn: '1 / -1' }}>
        <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
          Findings by Severity
        </p>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {(Object.entries(survey.bySeverity) as [DebtSeverity, number][]).map(([sev, count]) => {
            const cfg = SEVERITY_CONFIG[sev];
            return (
              <div key={sev}>
                <p style={{ fontSize: 24, fontWeight: 700, color: cfg.color }}>{count}</p>
                <p style={{ fontSize: 13, color: C.textSec }}>{cfg.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Findings View ──

function FindingsView({ findings }: { findings: DebtFinding[] }) {
  const [filterType, setFilterType] = useState<DebtType | 'all'>('all');
  const [filterSev, setFilterSev] = useState<DebtSeverity | 'all'>('all');

  const filtered = findings.filter((f) => {
    if (filterType !== 'all' && f.type !== filterType) return false;
    if (filterSev !== 'all' && f.severity !== filterSev) return false;
    return true;
  });

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as DebtType | 'all')}
          style={{ padding: '6px 10px', fontSize: 13, border: `1px solid ${C.border}`, borderRadius: 4, background: C.bgCard }}
        >
          <option value="all">All Types</option>
          {Object.entries(DEBT_TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={filterSev}
          onChange={(e) => setFilterSev(e.target.value as DebtSeverity | 'all')}
          style={{ padding: '6px 10px', fontSize: 13, border: `1px solid ${C.border}`, borderRadius: 4, background: C.bgCard }}
        >
          <option value="all">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <span style={{ fontSize: 13, color: C.textMuted, alignSelf: 'center' }}>
          {filtered.length} finding{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Findings table */}
      {filtered.length === 0 ? (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 40, textAlign: 'center' }}>
          <p style={{ fontSize: 14, color: C.textMuted }}>No findings match the current filters.</p>
        </div>
      ) : (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}`, textAlign: 'left' }}>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: C.textSec }}>Severity</th>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: C.textSec }}>Type</th>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: C.textSec }}>File</th>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: C.textSec }}>Description</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => {
                const sevCfg = SEVERITY_CONFIG[f.severity];
                return (
                  <tr key={f.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, color: sevCfg.color, background: sevCfg.bg,
                        padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase',
                      }}>
                        {sevCfg.label}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: C.text }}>{DEBT_TYPE_LABELS[f.type]}</td>
                    <td style={{ padding: '8px 12px', color: C.textSec, fontFamily: 'monospace', fontSize: 12 }}>
                      {f.file}{f.line ? `:${f.line}` : ''}
                    </td>
                    <td style={{ padding: '8px 12px', color: C.text, maxWidth: 400 }}>
                      {f.description}
                      {f.suggestedFix && (
                        <span style={{ display: 'block', fontSize: 12, color: C.pass, marginTop: 2 }}>
                          Fix: {f.suggestedFix}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Hotspots View ──

function HotspotsView({ survey }: { survey: DebtSurvey }) {
  const hotspots = survey.hotspots || [];
  const maxCount = hotspots.length > 0 ? Math.max(...hotspots.map((h) => h.count)) : 1;

  return (
    <div>
      <p style={{ fontSize: 14, color: C.textSec, marginBottom: 16 }}>
        Files with the most findings — focus cleanup effort here.
      </p>
      {hotspots.length === 0 ? (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 40, textAlign: 'center' }}>
          <p style={{ fontSize: 14, color: C.textMuted }}>No hotspots identified.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {hotspots.map((h) => {
            const pct = Math.round((h.count / maxCount) * 100);
            const barColor = h.count >= 5 ? C.fail : h.count >= 3 ? C.warn : C.pass;
            return (
              <div key={h.file} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontFamily: 'monospace', color: C.text }}>{h.file}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: barColor }}>{h.count}</span>
                </div>
                <div style={{ height: 6, background: '#F4F4F5', borderRadius: 3 }}>
                  <div style={{ height: 6, width: `${pct}%`, background: barColor, borderRadius: 3, transition: 'width 0.3s' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Briefing View ──

function BriefingView({ briefing, surveyId, onBriefingGenerated }: {
  briefing: Briefing | null;
  surveyId: string;
  onBriefingGenerated: (b: Briefing) => void;
}) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      const result = await generateDebtBriefingAction(surveyId);
      onBriefingGenerated({
        id: result.briefingId,
        surveyId,
        ...result.briefing,
        generatedAt: new Date().toISOString(),
      } as Briefing);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate briefing');
    } finally {
      setGenerating(false);
    }
  }

  if (!briefing) {
    return (
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 40, textAlign: 'center' }}>
        <p style={{ fontSize: 16, color: C.text, fontWeight: 600, marginBottom: 8 }}>No briefing generated yet</p>
        <p style={{ fontSize: 14, color: C.textSec, marginBottom: 16 }}>
          Generate a prioritized cleanup briefing from the latest survey results.
        </p>
        <button
          onClick={handleGenerate}
          disabled={generating}
          style={{
            padding: '10px 24px', fontSize: 14, fontWeight: 600,
            color: '#fff', background: generating ? C.textMuted : C.rose,
            border: 'none', borderRadius: 4, cursor: generating ? 'default' : 'pointer',
          }}
        >
          {generating ? 'Generating...' : 'Generate Briefing'}
        </button>
        {error && <p style={{ fontSize: 13, color: C.fail, marginTop: 8 }}>{error}</p>}
      </div>
    );
  }

  const { executiveSummary, sections } = briefing;
  const scoreColor = executiveSummary.debtScore >= 80 ? C.pass : executiveSummary.debtScore >= 50 ? C.warn : C.fail;

  return (
    <div>
      {/* Executive Summary */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
              Cleanup Briefing
            </p>
            <p style={{ fontSize: 18, fontWeight: 700, color: C.text }}>
              Score: <span style={{ color: scoreColor }}>{executiveSummary.debtScore}/100</span>
              <span style={{ fontSize: 14, fontWeight: 400, color: C.textSec, marginLeft: 8 }}>
                ({executiveSummary.scoreLabel})
              </span>
            </p>
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              padding: '6px 16px', fontSize: 13,
              color: C.rose, background: C.roseSoft,
              border: `1px solid ${C.rose}`, borderRadius: 4,
              cursor: generating ? 'default' : 'pointer',
            }}
          >
            {generating ? 'Regenerating...' : 'Regenerate'}
          </button>
        </div>
        <p style={{ fontSize: 14, color: C.text, lineHeight: 1.6, marginBottom: 8 }}>
          {executiveSummary.recommendation}
        </p>
        <p style={{ fontSize: 13, color: C.textSec }}>
          {executiveSummary.totalFindings} findings across {executiveSummary.filesScanned} files &middot;
          Top priority: {executiveSummary.topPriority}
        </p>
        {briefing.generatedAt && (
          <p style={{ fontSize: 12, color: C.textMuted, marginTop: 8 }}>
            Generated {new Date(briefing.generatedAt).toLocaleString()}
          </p>
        )}
      </div>

      {/* Priority Sections */}
      {sections.map((section) => (
        <div key={section.priority} style={{
          background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20, marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: '#fff',
              background: section.priority <= 2 ? C.fail : section.priority <= 4 ? C.warn : C.textMuted,
              padding: '2px 8px', borderRadius: 4,
            }}>
              P{section.priority}
            </span>
            <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{section.title}</span>
            <span style={{ fontSize: 13, color: C.textMuted, marginLeft: 'auto' }}>
              {section.findings.length} finding{section.findings.length !== 1 ? 's' : ''}
            </span>
          </div>
          <p style={{ fontSize: 13, color: C.textSec, marginBottom: 12, lineHeight: 1.5 }}>
            {section.rationale}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {section.findings.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '4px 0', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
                <span style={{ fontSize: 12, fontFamily: 'monospace', color: C.textSec, minWidth: 200, flexShrink: 0 }}>
                  {f.file}{f.line ? `:${f.line}` : ''}
                </span>
                <span style={{ fontSize: 13, color: C.text }}>
                  {f.description}
                  {f.suggestedFix && (
                    <span style={{ color: C.pass, marginLeft: 8, fontSize: 12 }}>
                      — {f.suggestedFix}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── History View ──

function HistoryView({ history }: { history: DebtSurvey[] }) {
  return (
    <div>
      <p style={{ fontSize: 14, color: C.textSec, marginBottom: 16 }}>
        Survey history — track debt score over time.
      </p>
      {history.length === 0 ? (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 40, textAlign: 'center' }}>
          <p style={{ fontSize: 14, color: C.textMuted }}>No survey history available.</p>
        </div>
      ) : (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}`, textAlign: 'left' }}>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: C.textSec }}>Date</th>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: C.textSec }}>Phase</th>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: C.textSec }}>Score</th>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: C.textSec }}>Findings</th>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: C.textSec }}>Resolved</th>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: C.textSec }}>New</th>
              </tr>
            </thead>
            <tbody>
              {history.map((s) => {
                const scoreColor = s.debtScore >= 80 ? C.pass : s.debtScore >= 50 ? C.warn : C.fail;
                return (
                  <tr key={s.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '8px 12px', color: C.text }}>
                      {new Date(s.scannedAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '8px 12px', color: C.textSec, textTransform: 'capitalize' }}>
                      {s.phase}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ fontWeight: 700, color: scoreColor }}>{s.debtScore}</span>
                    </td>
                    <td style={{ padding: '8px 12px', color: C.text }}>{s.findings}</td>
                    <td style={{ padding: '8px 12px', color: C.pass }}>{s.resolved}</td>
                    <td style={{ padding: '8px 12px', color: s.newSinceLastSurvey > 0 ? C.warn : C.textSec }}>
                      {s.newSinceLastSurvey > 0 ? `+${s.newSinceLastSurvey}` : '0'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Score Ring ──

function DebtScoreRing({ score, color }: { score: number; color: string }) {
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <svg width={80} height={80} viewBox="0 0 80 80">
      <circle cx="40" cy="40" r={radius} fill="none" stroke="#E4E4E7" strokeWidth="6" />
      <circle
        cx="40" cy="40" r={radius} fill="none"
        stroke={color} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        transform="rotate(-90 40 40)"
      />
      <text x="40" y="44" textAnchor="middle" fontSize="18" fontWeight="700" fill={color}
        style={{ fontFamily: "'DM Sans', sans-serif" }}>
        {score}
      </text>
    </svg>
  );
}
